//#region Imports

import { booleanAttribute, Component, EventEmitter, Input, OnChanges, OnInit, Output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ButtonComponent } from '../button/button.component';
import { IconComponent } from '../icon/icon.component';
import { OutsideClick } from '../../directives/outside-click/outside-click.directive';
import { ButtonTypeEnum } from '../button/types/button-type.enum';
import { ButtonRadiusEnum } from '../button/types/button-radius.enum';
import { Category } from '../../models/interfaces/category.interface';
import { MultiSelectComponent } from '../multi-select/multi-select.component';
import { CourseLocation } from '../../models/enums/course-location.enum';
import { CourseType } from '../../models/enums/course-type.enum';
import { CourseFilters } from '../../models/interfaces/course-filters.interface';

//#endregion

export interface TypeFilterOption {
  label: string;
  value: CourseType;
  icon: string;
}

export interface LocationFilterOption {
  label: string;
  value: CourseLocation;
  icon: string;
}

@Component({
  selector: 'l-course-filter',
  standalone: true,
  templateUrl: './course-filter.component.html',
  imports: [
    FormsModule,
    ButtonComponent,
    IconComponent,
    OutsideClick,
    MultiSelectComponent
  ],
})
export class CourseFilterComponent implements OnInit, OnChanges {

  //#region Public Properties

  @Input({ required: true }) 
  public categories: Category[] = [];

  @Input() 
  public selectedCategories: Category[] | null = null;

  @Input({ transform: booleanAttribute })
  public showTypeFilter: boolean = false;

  @Input({ transform: booleanAttribute })
  public showLocationFilter: boolean = false;

  @Input() 
  public selectedType: CourseType | null = null;

  @Input() 
  public selectedLocation: CourseLocation | null = null;

  @Output() 
  public filterApplied: EventEmitter<CourseFilters> = new EventEmitter<CourseFilters>();

  public draftSelectedCategories: Category[] | null = null;
  public draftSelectedType: CourseType | null = null;
  public draftSelectedLocation: CourseLocation | null = null;

  public previousSelectedCategories: Category[] | null = null;
  public previousSelectedType: CourseType | null = null;
  public previousSelectedLocation: CourseLocation | null = null;

  public typeOptions: TypeFilterOption[] = [
    { label: 'Treinamento', value: CourseType.TRAINING, icon: 'flash_on' },
    { label: 'Curso', value: CourseType.COURSE, icon: 'school' }
  ];

  public locationOptions: LocationFilterOption[] = [
    { label: 'Presencial', value: CourseLocation.PRESENTIAL, icon: 'location_on' },
    { label: 'Online', value: CourseLocation.ONLINE, icon: 'headphones' },
    { label: 'Híbrido', value: CourseLocation.HYBRID, icon: 'hub' }
  ];

  public open: boolean = false;

  public isCategoryPanelOpen: boolean = false;

  public buttonTypeEnum: typeof ButtonTypeEnum = ButtonTypeEnum;

  public buttonRadiusEnum: typeof ButtonRadiusEnum = ButtonRadiusEnum;

  //#endregion

  //#region Public Methods

  public ngOnInit(): void {
    this.initializeState();
  }

  public ngOnChanges(): void {
    this.selectedCategories = this.normalizeSelectedCategories(this.selectedCategories);
    if (!this.open) this.syncTemporaryState();
  }

  public toggleOpen(): void {
    if (this.open) {
      this.close();
    } else {
      this.syncTemporaryState();
      this.open = true;
    }
  }

  public close(): void {
    this.restorePreviousState();
    this.isCategoryPanelOpen = false;
    this.open = false;
  }
  
  public clearFilter(): void {
    this.draftSelectedCategories = [];
    this.draftSelectedType = null;
    this.draftSelectedLocation = null;
  }

  public applyFilter(): void {
    this.selectedCategories = this.cloneArray(this.draftSelectedCategories);
    this.selectedType = this.draftSelectedType;
    this.selectedLocation = this.draftSelectedLocation;
    
    this.filterApplied.emit({
      categories: this.selectedCategories,
      type: this.selectedType,
      location: this.selectedLocation
    });
    
    this.syncTemporaryState();
    this.isCategoryPanelOpen = false;
    this.open = false;
  }

  //#endregion

  //#region Private Methods

  private initializeState(): void {
    this.selectedCategories = this.normalizeSelectedCategories(this.selectedCategories);
    this.syncTemporaryState();
  }

  private normalizeSelectedCategories(categories: Category[] | null): Category[] | null {
    if (!categories) return null;
    return this.categories.filter(category => categories.some(selected => selected.id === category.id));
  }

  private syncTemporaryState(): void {
    this.previousSelectedCategories = this.cloneArray(this.selectedCategories);
    this.draftSelectedCategories = this.cloneArray(this.selectedCategories);
    
    this.previousSelectedType = this.selectedType;
    this.draftSelectedType = this.selectedType;
    
    this.previousSelectedLocation = this.selectedLocation;
    this.draftSelectedLocation = this.selectedLocation;
  }

  private restorePreviousState(): void {
    this.selectedCategories = this.cloneArray(this.previousSelectedCategories);
    this.draftSelectedCategories = this.cloneArray(this.previousSelectedCategories);
    
    this.selectedType = this.previousSelectedType;
    this.draftSelectedType = this.previousSelectedType;
    
    this.selectedLocation = this.previousSelectedLocation;
    this.draftSelectedLocation = this.previousSelectedLocation;
  }

  private cloneArray<T>(array: T[] | null): T[] | null {
    return array ? [...array] : null;
  }

  //#endregion
}
